// Loading what a run is built from: AxisCare visits, the clients they belong
// to, and those clients' authorisation history. Shared by the new-run wizard
// and resync; the rules themselves are lib/billing/claimLines.js.

import { supabase } from '../../../lib/supabase';
import { getVisitsByDateRange } from '../../../lib/axiscare';
import { visitsFromResponse } from '../../../lib/axiscareVisits';
import { matchVisitClients, isBillableVisit, clientOf, serviceDateOf } from '../../../lib/billing/claimLines';
import { resolvePriorAuth, authHistoryByClient, CLAIM_AUTH_TYPE } from '../../../lib/priorAuth';

/** Every visit AxisCare has for the range, in billing mode (the merge seam applied). Throws on error. */
export const fetchBillingVisits = async (from, to) => {
  const { data, error } = await getVisitsByDateRange(from, to, { mode: 'billing' });
  if (error) throw error;
  return visitsFromResponse(data?.results?.visits);
};

/** Whether a visit may bill at all, on this basis — lib/billing/claimLines.js. */
export const billableVisits = (arr, basis) => arr.filter(v => isBillableVisit(v, basis));

// Which client each visit belongs to. The matching rule — AxisCare id, then
// name marked for review, never a test or duplicate record — is in
// lib/billing/claimLines.js, shared with the Monday run.
export const fetchClientsForMatching = async () => {
  const { data: allClients, error: allClientsErr } = await supabase
    .from('clients')
    .select('id, client_id, axiscare_client_id, medicaid_id, diagnosis_code, first_name, last_name, dma_80_pa_number, pa_expiry_date, service_type, is_test, superseded_by, current_status');
  if (allClientsErr) console.error('buildClientMap: failed to load clients', allClientsErr);
  return allClients || [];
};

export const buildClientMap = async (completedVisits, clients = null) =>
  matchVisitClients(completedVisits, clients || await fetchClientsForMatching());

// Authorization history for everyone in the run, so each line can be given
// the authorization that was in force on its own service date rather than
// whichever is current now. Superseded rows are wanted here: a renewal
// supersedes its predecessor, and an older service date bills under the
// predecessor. Loaded once per run rather than per line.
export const loadAuthHistory = async (cMap) => {
  const ids = [...new Set(Object.values(cMap).map(c => c?.id).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase
    .from('prior_auth_history')
    .select('client_id, auth_type, auth_number, submitted_date, effective_date, expiry_date, status, created_at')
    .in('client_id', ids)
    .eq('auth_type', CLAIM_AUTH_TYPE);
  if (error) { console.error('loadAuthHistory failed', error); return {}; }
  return authHistoryByClient(data);
};

/** The authorisation in force on a visit's own service date. */
export const authFor = (v, cMap, histBy) => {
  const hc = clientOf(v, cMap);
  return resolvePriorAuth({
    history: histBy[hc.id] || [],
    client: hc,
    date: serviceDateOf(v),
  });
};
